import React from 'react';
import { File, FileText, Film, Globe, Image as ImageIcon, ShoppingBag, StickyNote, UserRound } from 'lucide-react';
import type { LibrarySource } from '@/lib/library/sources';
import { cn } from '@/lib/utils';

/**
 * Monochrome mark for a Library source, drawn in currentColor so it sits in
 * ink on the tile badge and on filter chips. Platform marks are simplified
 * line drawings on a 24 grid with a 1.75 stroke to match lucide.
 */
export function SourceGlyph({ source, className }: { source: LibrarySource; className?: string }) {
  const cls = cn('h-4 w-4 shrink-0', className);
  const line = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.75, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (source) {
    case 'instagram_reel':
    case 'instagram_post':
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden>
          <rect x="3.5" y="3.5" width="17" height="17" rx="5" {...line} />
          <circle cx="12" cy="12" r="3.9" {...line} />
          <circle cx="17.1" cy="6.9" r="1.05" fill="currentColor" />
        </svg>
      );
    case 'tiktok':
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden>
          <path d="M13.5 4v10.6a3.6 3.6 0 1 1-3.6-3.6" {...line} />
          <path d="M13.5 4c.4 2.5 2.2 4.3 4.8 4.6" {...line} />
        </svg>
      );
    case 'x':
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden>
          <path d="M4.5 4.5 19.5 19.5M19.5 4.5 4.5 19.5" {...line} strokeWidth={1.9} />
        </svg>
      );
    case 'youtube':
    case 'vimeo':
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden>
          <rect x="2.75" y="5.5" width="18.5" height="13" rx="4" {...line} />
          <path d="M10.25 9.4v5.2l4.4-2.6z" fill="currentColor" />
        </svg>
      );
    case 'pinterest':
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden>
          <circle cx="12" cy="12" r="8.75" {...line} />
          <path d="M11.2 9.2c.3-1.2 1.4-1.9 2.6-1.7 1.6.3 2.3 1.9 1.8 3.5-.5 1.7-2 2.5-3.3 2M11.6 10.4 9.6 19" {...line} />
        </svg>
      );
    case 'threads':
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden>
          <path d="M16.5 11.2c-.4-2.3-2-3.4-4.2-3.4-2.6 0-4.3 1.9-4.3 4.4 0 2.6 1.8 4.4 4.3 4.4 2.3 0 3.6-1.2 3.6-2.8 0-1.5-1.2-2.4-3.2-2.4-1.5 0-2.4.7-2.4 1.7 0 .9.8 1.5 1.9 1.5" {...line} />
          <path d="M19 9.5C18 5.8 15.4 3.75 12 3.75 7.4 3.75 4.25 7.2 4.25 12S7.4 20.25 12 20.25c3.1 0 5.4-1.4 6.5-3.8" {...line} />
        </svg>
      );
    case 'linkedin':
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden>
          <rect x="3.5" y="3.5" width="17" height="17" rx="3" {...line} />
          <path d="M8 10.5V16M8 8v.01M11.5 16v-5.5M11.5 13c0-1.5 1-2.5 2.3-2.5S16 11.5 16 13v3" {...line} />
        </svg>
      );
    case 'facebook':
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden>
          <circle cx="12" cy="12" r="8.75" {...line} />
          <path d="M13.5 20.5V12.5h2.3M13.5 12.5v-2c0-1.2.6-1.9 1.9-1.9h1.1M11 12.5h2.5" {...line} />
        </svg>
      );
    case 'reddit':
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden>
          <ellipse cx="12" cy="14" rx="7.5" ry="5.25" {...line} />
          <path d="M12 8.75 13.2 4l3.3.8" {...line} />
          <circle cx="17.6" cy="5" r="1.1" {...line} />
          <path d="M9.3 16.1c1.6 1 3.8 1 5.4 0" {...line} />
          <circle cx="9.4" cy="13.3" r=".9" fill="currentColor" />
          <circle cx="14.6" cy="13.3" r=".9" fill="currentColor" />
        </svg>
      );
    case 'profile':
      return <UserRound className={cls} strokeWidth={1.75} aria-hidden />;
    case 'product':
      return <ShoppingBag className={cls} strokeWidth={1.75} aria-hidden />;
    case 'web':
      return <Globe className={cls} strokeWidth={1.75} aria-hidden />;
    case 'image':
      return <ImageIcon className={cls} strokeWidth={1.75} aria-hidden />;
    case 'video':
      return <Film className={cls} strokeWidth={1.75} aria-hidden />;
    case 'pdf':
      return <FileText className={cls} strokeWidth={1.75} aria-hidden />;
    case 'file':
      return <File className={cls} strokeWidth={1.75} aria-hidden />;
    case 'note':
      return <StickyNote className={cls} strokeWidth={1.75} aria-hidden />;
    default:
      return null;
  }
}
